import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Asset } from '../types'

/**
 * A panorama or a hairline crop would be drawn as a stripe nobody can click.
 * Clamping keeps every card a usable shape; the picture inside is fitted, not
 * cropped, so nothing is lost.
 */
const MIN_RATIO = 0.4
const MAX_RATIO = 3
/**
 * How far a row's height may drift from the target to make it end flush.
 *
 * It depends on how many images are in the row, and that matters more than it
 * sounds. A wide screen fits eight or nine across, so the adjustment is spread
 * thin and no single row looks out of place. The Miro panel fits two or three,
 * where the same arithmetic swings a row's height by half — two portraits
 * stretch tall, a landscape beside them collapses, and scrolling turns into a
 * series of lurches.
 *
 * So a short row is allowed less licence and accepts a small gap at its end
 * instead. Flush edges are worth having; they are not worth a page that
 * bounces.
 */
const SLACK_PER_ITEM = 0.07
const MAX_SLACK = 0.4
/**
 * A row of four or more may go further than its slack before it is allowed to
 * end short (2026-10-02): on a wide screen a gap at the end of a row is far
 * more noticeable than one row being a little taller or shorter than the
 * rest. Two or three across -- the Miro panel -- keep the rule above.
 */
const WIDE_ROW_ITEMS = 4
const WIDE_ROW_UP = 0.5
const WIDE_ROW_DOWN = 0.4

export interface JustifiedItem {
  asset: Asset
  width: number
  height: number
}

/**
 * Measure the element the grid is drawn into, and keep measuring.
 *
 * Whichever element that is NOW (2026-10-02). The grid draws placeholders
 * while it loads and the real rows afterwards -- two different elements --
 * and the measuring used to be attached once, to the first: it went on
 * watching the placeholders after they were gone, so the width the rows were
 * cut to was frozen at whatever it was on arrival. Moving the window to a
 * screen of another size then left rows running past the right edge, or
 * stopping short of it. A callback ref follows the element as it changes;
 * the window's own resize is listened to as well, belt and braces.
 */
export function useContainerWidth<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null)
  const ref = useCallback((node: T | null) => setElement(node), [])
  const [width, setWidth] = useState(0)

  useEffect(() => {
    if (!element) return
    const measure = () => {
      const next = element.getBoundingClientRect().width
      setWidth((current) => (Math.abs(current - next) > 0.5 ? next : current))
    }
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    window.addEventListener('resize', measure)
    measure()
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [element])

  return { ref, width }
}

/**
 * Lay images out in rows that fill the width exactly.
 *
 * Images of mixed proportions drawn at one height and wrapped leave a ragged
 * right edge and a different gap at the end of every row — which is what a
 * shelf of packaging looked like. A justified layout is the answer galleries
 * have used for years: fill a row greedily at roughly the target height, then
 * adjust that row's height so it ends flush.
 *
 * The cost is that rows are no longer all the same height. Inside a row they
 * are identical, which is where comparison actually happens, and the drift is
 * bounded — without a bound, a row holding one panorama would either fill the
 * screen or shrink to a thread.
 *
 * The last row is left at the target height. Stretching two images across a
 * whole screen to make an edge tidy is the one thing that looks worse than the
 * ragged edge being fixed.
 */
export function useJustifiedRows(
  assets: Asset[],
  containerWidth: number,
  targetHeight: number,
  gap: number,
): JustifiedItem[][] {
  return useMemo(() => {
    if (containerWidth <= 0 || assets.length === 0) return []

    const ratioOf = (asset: Asset) => {
      const raw = asset.width > 0 && asset.height > 0 ? asset.width / asset.height : 0.8
      return Math.min(MAX_RATIO, Math.max(MIN_RATIO, raw))
    }

    const rows: Asset[][] = []
    let row: Asset[] = []
    let ratioSum = 0

    // How far a height is from the target, as a proportion either way:
    // half as tall and twice as tall are equally far.
    const off = (height: number) => Math.abs(Math.log(height / targetHeight))

    for (const asset of assets) {
      const ratio = ratioOf(asset)
      const widthIfAdded = (ratioSum + ratio) * targetHeight + row.length * gap
      // A row always takes at least one image, however wide that image is.
      if (row.length > 0 && widthIfAdded > containerWidth) {
        /*
         * The next image does not fit at the target height. Two ways to end
         * the row: without it, the row grows taller to reach the edge; with
         * it, the row shrinks a little to fit it in. Take whichever leaves the
         * row nearer the target (2026-10-02). Asking only "does it fit?" sent
         * a wide picture -- a mouse, 3:1 -- down to the next row when taking
         * it would have meant shrinking the row by 1%, and left five pictures
         * that could not stretch far enough to reach the edge.
         */
        const without = (containerWidth - (row.length - 1) * gap) / ratioSum
        const withIt = (containerWidth - row.length * gap) / (ratioSum + ratio)
        if (off(withIt) < off(without)) {
          row.push(asset)
          rows.push(row)
          row = []
          ratioSum = 0
        } else {
          rows.push(row)
          row = [asset]
          ratioSum = ratio
        }
      } else {
        row.push(asset)
        ratioSum += ratio
      }
    }
    if (row.length > 0) rows.push(row)

    return rows.map((items, index) => {
      const sum = items.reduce((total, asset) => total + ratioOf(asset), 0)
      // One pixel back. A row computed to fill the width exactly has nothing
      // left for a rounding error, a fractional container width, or a scrollbar
      // that appears the moment the content gets tall — and a row one pixel too
      // wide is as broken-looking as one fifty pixels too wide.
      const usable = containerWidth - 1
      const spare = usable - (items.length - 1) * gap
      const isLast = index === rows.length - 1
      const exact = spare / sum
      const slack = Math.min(MAX_SLACK, SLACK_PER_ITEM * items.length)
      const wide = items.length >= WIDE_ROW_ITEMS
      const up = wide ? Math.max(slack, WIDE_ROW_UP) : slack
      const down = wide ? Math.max(slack, WIDE_ROW_DOWN) : slack
      const height = isLast
        ? // Only pull the last row DOWN to fit; never stretch a couple of
          // images across the whole screen to tidy an edge.
          Math.min(targetHeight, exact)
        : Math.min(
            targetHeight * (1 + up),
            Math.max(targetHeight * (1 - down), exact),
          )

      const drawn = Math.round(height)
      const widths = items.map((asset) => Math.round(height * ratioOf(asset)))

      // Rounding each width on its own can put the row a few pixels over the
      // edge. The difference is given to the widest image, where a pixel or two
      // is invisible, so the row adds up to exactly what there is room for.
      //
      // ONLY a rounding error, though — at most one pixel per image. A row that
      // is deliberately short must stay short, and two of them are: the last
      // row, which is never stretched, and any row whose height the slack bound
      // pulled back. Treating their leftover width as rounding handed the whole
      // of it to one card, which then drew a small picture in the middle of an
      // enormous blank box while its neighbours looked normal. The picture is
      // fitted rather than cropped, so every pixel the card gains that the
      // image does not is blank.
      const total = widths.reduce((a, b) => a + b, 0) + (items.length - 1) * gap
      const over = total - usable
      // Too wide is always corrected: a row that overflows its container is the
      // visible breakage. Too narrow is corrected only when it is rounding —
      // otherwise the row is short on purpose and stays that way.
      if (over > 0 || (over < 0 && -over <= items.length + 1)) {
        let widest = 0
        for (let i = 1; i < widths.length; i += 1) if (widths[i] > widths[widest]) widest = i
        widths[widest] = Math.max(1, widths[widest] - over)
      }

      return items.map((asset, i) => ({ asset, width: widths[i], height: drawn }))
    })
  }, [assets, containerWidth, targetHeight, gap])
}
